import { getPatternByIndex } from '../utils/patternsUtils'
import LineAndBarChartViz from '../vizs/LineAndBarChartViz'
import BaseComponent from './BaseComponent'
/**
 * {-EN-}
 * # {componentLabel} (`class {componentClassName}`)
 * Used to display data using a number of bars and/or lines, each of which represent a particular category. A combination of bars and lines in the same visualization can be useful when comparing values in different categories, since the combination gives a clear view of which category is higher or lower. An example of this can be seen when comparing the projected sales with the actual sales for different time periods.
 *
 * ### Example Usage
 * ```javascript
 *
 *   import {LineAndBarChart} from 'sdv-lib'
 *
 *   // Instantiate the chart with a DOM element, a data Array and a config Object
 *   let myChart = new LineAndBarChart($el, data, config);
 *
 * ```
 *
 * ### Data structure {.section-data-structure}
 * The tabular data for this chart should be a table with 3 or more columns.
 *
 * Each row represents a point in the X axis. The first column should be the id/label of each point.
 *
 * The second column represents the values for the Bars in the chart.
 *
 * The rest of the columns should represent the values for each of the lines in the chart.
 *
 * |  categroy    | bar  |  line1   |  line2   |
 * | --------   | ------ |  ------  |  ------  |
 * |   a       |    22.4|      15.4|      43.2|
 * |   b      |    52.3|      29.3|      12.8|
 * |   c      |    12.8|      34.1|      61.4|
 * |   d      |    26.0|      12.3|      14.5|
 *
 * {.data-table}
 *
 * ### Curve types {.curves}
 * There are three different type of curves available:
 *
 * - **smooth**: curves will interpolate between the data points.
 * - **sharp**: curves will be composed of straight line segments.
 * - **smooth&ndash;m**: curves will preserve monotonicity in y, assuming monotonicity in x.
 * <div>
 *  <img src="./developer-guide-assets/line-chart/curves-example.png"/>
 * </div>
 *
 * {-ES-}
 * # {componentLabelEs} (`class {componentClassName}`)
 * Se utiliza para mostrar datos utilizando varias barras y/o líneas, cada una de las cuales representa una categoría particular. Una combinación de barras y líneas en la misma visualización puede resultar útil al comparar valores en diferentes categorías, ya que la combinación ofrece una vista clara de qué categoría es mayor o menor. Un ejemplo de esto se puede ver al comparar las ventas proyectadas con las ventas reales para diferentes períodos de tiempo.
 *
 * ### Ejemplo de uso
 * ```javascript
 *
 *   import {LineAndBarChart} from 'sdv-lib'
 *
 *   // Instantiate the chart with a DOM element, a data Array and a config Object
 *   let myChart = new LineAndBarChart($el, data, config);
 *
 * ```
 *
 * ### Estructura de datos {.section-data-structure}
 * Los datos tabulares de este gráfico deben ser una tabla con 3 o más columnas.
 *
 * Cada fila representa un punto en el eje X. La primera columna debe ser la identificación/etiqueta de cada punto.
 *
 * La segunda columna representa los valores de las barras en el gráfico.
 *
 * El resto de las columnas deben representar los valores de cada una de las líneas del gráfico.
 *
 * |  categoría    | bar  |  line1   |  line2   |
 * | --------   | ------ |  ------  |  ------  |
 * |   a       |    22.4|      15.4|      43.2|
 * |   b      |    52.3|      29.3|      12.8|
 * |   c      |    12.8|      34.1|      61.4|
 * |   d      |    26.0|      12.3|      14.5|
 *
 * {.data-table}
 *
 * ### Tipos de curvas {.curves}
 * Hay tres tipos de curvas disponibles:
 *
 * - **smooth**: Las curvas se interpolarán entre los puntos de datos.
 * - **sharp**: Las curvas estarán compuestas por segmentos de línea recta.
 * - **smooth&ndash;m**: Las curvas conservan la monotonicidad en y, asumiendo monotonicidad en x
 * <div>
 *  <img src="./developer-guide-assets/line-chart/curves-example.png"/>
 * </div>
 *
 * */
export default class LineAndBarChartComponent extends BaseComponent {
  static vizClass = LineAndBarChartViz
  static componentLabel = 'Multiple lines + bars'
  static componentLabelEs = 'Varias líneas + barras'
  static dataFormat = [
    { index: 0, axis: 'x0', types: [String], role: 'id', roleLabel: 'Category (X)' },
    { index: 1, axis: 'y0', types: [Number], role: 'value', roleLabel: 'Bar value (Y)' },
    {
      index: -1,
      axis: 'y0',
      label: 'Line',
      types: [Number],
      role: 'value',
      roleLabel: 'Line value (Y)',
    },
  ]
  static defaultConfig = {
    title: this.componentLabel,
    simple: true,
    ItemsHelper: {
      colorScale: {
        nonEditable: [true, true],
        value: ['categorical2', 'categorical'],
        options: [['categorical2'], ['categorical']],
        multiValueIds: ['Lines', 'Bars'],
      },
    },
    AxisHelper: {
      x: {
        /**
         * {-EN-} Show/hide labels for each bar on the X axis.
         * {-ES-} Mostrar/ocultar etiquetas para cada barra en el eje X.
         */
        showTickValues: {
          default: false,
        },
      },
    },
    TooltipBehavior: {
      axis: {
        y: {
          tooltipValueFormat: {
            exposed: true,
            nonEditable: false,
          },
        },
      },
    },
  }
  static dataRequirements = {
    recomendedMaxRows: 10,
    maxRows: 30,
    acceptRoleRow: true,
  }

  static accessibilityRules = {
    ...super.accessibilityRules,
    textures: {
      ...super.accessibilityRules.textures,
      check: (config, data) => {
        if (!config.hasColorPatterns) return true
        const colorScale = config.ItemsHelper.colorScale?.[1]
        if (colorScale !== 'categorical') return true
        const colorDict = config.ItemsHelper.colorDict?.[1]
        if (colorDict) {
          const colorKey =
            typeof config.ItemsHelper.colorKey === 'function' ? 'id' : config.ItemsHelper.colorKey
          const groupedDataKeys = new Set(data.bars.map(d => d[colorKey]))
          let totalPatterns = [...groupedDataKeys].reduce((accumulator, current) => {
            accumulator += colorDict[current]?.pattern?.id ? 1 : 0
            return accumulator
          }, 0)
          return totalPatterns >= groupedDataKeys.size - 1
        }
        return false
      },
      applyAutoConfigChange: function (config, data, colors) {
        if (this.check(config, data)) return {}
        const oldConfig = {
          ItemsHelper: {
            ...config.ItemsHelper,
            colorDict: [...(config.ItemsHelper.colorDict ? config.ItemsHelper.colorDict : [])],
          },
        }
        const colorKey =
          typeof config.ItemsHelper.colorKey === 'function' ? 'id' : config.ItemsHelper.colorKey
        const groupedDataKeys = new Set(data.bars.map(d => d[colorKey]))
        const newConfig = {
          ItemsHelper: {
            ...oldConfig.ItemsHelper,
            colorDict: [
              oldConfig.ItemsHelper.colorDict[0],
              [...groupedDataKeys].reduce((accumulator, current, i) => {
                accumulator[current] = {
                  ...colors[current],
                  pattern: getPatternByIndex(i),
                }
                return accumulator
              }, {}),
            ],
          },
        }
        return {
          oldConfig,
          newConfig,
        }
      },
    },
  }
}
